import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/auth.context';
import { useTenantStore } from '../stores/tenant.store';
import { usePortalView } from '../stores/portal-view.store';

interface StudentIdentity {
  personId: string | null;
  /** The student's own name and code, from the SAME lookup (no extra read).
   *  In «Ver portal» the name stays null: the view shows the student by code. */
  firstName: string | null;
  lastName: string | null;
  studentCode: string | null;
  loading: boolean;
  /** A code, not text: nothing shows it to a person today. */
  error: 'not_found' | 'load_failed' | null;
}

interface OwnPerson {
  personId: string;
  firstName: string | null;
  lastName: string | null;
  studentCode: string | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

/**
 * Resolves the Firestore personId for the currently authenticated student,
 * by querying persons where authUid == currentUser.uid.
 *
 * «Ver portal»: while staff view a student's portal (portal-view store), the
 * student is the view's person and the session uid is never used — it would
 * find the staff member's own person. The server resolves the same person from
 * the view on every read; this only labels the client's queries.
 */
export function useStudentIdentity(): StudentIdentity {
  // All hooks must be called unconditionally (Rules of Hooks)
  const view = usePortalView();
  const viewedPersonId = view?.personId ?? null;
  const { user } = useAuth();
  const { currentTenantId } = useTenantStore();
  const [own, setOwn] = useState<OwnPerson | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<StudentIdentity['error']>(null);

  useEffect(() => {
    // Portal view: no lookup by the session uid.
    if (viewedPersonId) {
      setLoading(false);
      return;
    }

    if (!user || !currentTenantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const personsRef = collection(db, 'tenants', currentTenantId, 'people');
    const q = query(personsRef, where('authUid', '==', user.uid));

    getDocs(q)
      .then((snap) => {
        const found = snap.docs[0];
        if (found) {
          const d = found.data();
          setOwn({
            personId: found.id,
            firstName: str(d['firstName']),
            lastName: str(d['lastName']),
            studentCode: str(d['studentCode']),
          });
        } else {
          setError('not_found');
        }
      })
      .catch((err) => {
        setError('load_failed');
        console.error('[useStudentIdentity]', err);
      })
      .finally(() => setLoading(false));
  }, [viewedPersonId, user, currentTenantId]);

  if (viewedPersonId) {
    return {
      personId: viewedPersonId,
      firstName: null,
      lastName: null,
      studentCode: view?.studentCode ?? null,
      loading: false,
      error: null,
    };
  }

  return {
    personId: own?.personId ?? null,
    firstName: own?.firstName ?? null,
    lastName: own?.lastName ?? null,
    studentCode: own?.studentCode ?? null,
    loading,
    error,
  };
}
